import React, { useEffect, useState } from "react";
import { Search, ShieldAlert, CheckCircle2, Camera, Fingerprint, AlertTriangle, Building2, TrendingUp, ArrowRight } from "lucide-react";

/**
 * Auto-cycling hero demo. Three "screens" tell the GuestIntel story:
 *   1. Front-desk lookup → match found across the group.
 *   2. New RedCorner incident being captured with evidence.
 *   3. Mission Control dashboard with KPIs and trend.
 *
 * Pure CSS / React state animation — no external deps.
 */

const SLIDES = ["lookup", "capture", "dashboard"];
const DEFAULT_INTERVAL_MS = 4200;

const SLIDE_CAPTIONS = {
  lookup: {
    title: "Cross-property guest lookup",
    body: "Front desk scans the ID. GuestIntel surfaces every prior record from every sister-property in 240 ms.",
  },
  capture: {
    title: "Capture an incident in under 2 minutes",
    body: "ID, reservation, photos and severity — mandatory evidence locked in, signed by the operator on duty.",
  },
  dashboard: {
    title: "Real-time group dashboard",
    body: "Property managers and Hotel Company Admins see records, criticals, Do Not Rent flags and trends across the entire portfolio.",
  },
};

function PaginationDots({ active, onSelect }) {
  return (
    <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5" data-testid="hero-demo-dots">
      {SLIDES.map((s, i) => (
        <button
          key={s}
          onClick={() => onSelect(i)}
          className={`h-1.5 rounded-full transition-all ${i === active ? "bg-teal w-6" : "bg-zinc-300 w-1.5 hover:bg-zinc-400"}`}
          aria-label={`Slide ${i + 1}`}
          data-testid={`hero-dot-${i}`}
        />
      ))}
    </div>
  );
}

/* ---- Slide 1: Lookup ---- */
function LookupSlide() {
  return (
    <div className="h-full w-full p-6 flex flex-col gap-4 bg-gradient-to-br from-zinc-50 to-white">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-md bg-primary text-white flex items-center justify-center">
          <Search className="w-3.5 h-3.5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Front desk · 21:04</div>
          <div className="text-sm font-display font-bold">Cross-property lookup</div>
        </div>
      </div>

      <div className="bg-white border border-border rounded-md px-3 py-2.5 text-sm text-zinc-700 flex items-center gap-2 shadow-sm">
        <Search className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="font-mono">james.l.harrington</span>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-teal bg-teal/10 px-2 py-0.5 rounded-sm">match</span>
      </div>

      <div className="space-y-2 mt-1">
        {[
          { hotel: "The Meridian Grand · NYC", sev: "Handle with grace", color: "bg-red-50 text-red-700",       icon: ShieldAlert },
          { hotel: "Meridian Riverside · Chicago", sev: "Take a moment",  color: "bg-orange-50 text-orange-700", icon: AlertTriangle },
          { hotel: "Meridian South · Miami",     sev: "Needs extra care", color: "bg-amber-50 text-amber-800",   icon: AlertTriangle },
        ].map((row, i) => {
          const Icon = row.icon;
          return (
            <div
              key={i}
              className="flex items-center gap-3 bg-white border border-border rounded-md px-3 py-2.5 animate-[fadeIn_0.45s_ease-out_both]"
              style={{ animationDelay: `${i * 120}ms` }}
            >
              <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs flex-1 font-medium truncate">{row.hotel}</span>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm flex items-center gap-1 ${row.color}`}>
                <Icon className="w-3 h-3" /> {row.sev}
              </span>
            </div>
          );
        })}
      </div>

      <div className="mt-auto flex items-center gap-2 text-[11px] text-muted-foreground">
        <CheckCircle2 className="w-3.5 h-3.5 text-teal" />
        Matched 3 records · pulled in 240 ms
      </div>
    </div>
  );
}

/* ---- Slide 2: Capture incident ---- */
function CaptureSlide() {
  const STEPS = [
    { icon: Fingerprint, label: "Government ID captured", done: true },
    { icon: Camera, label: "Reservation + 2 photos attached", done: true },
    { icon: AlertTriangle, label: "Severity · High · room damage", done: true },
  ];
  return (
    <div className="h-full w-full p-6 flex flex-col gap-4 bg-gradient-to-br from-red-50/60 via-white to-white">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-md bg-red-600 text-white flex items-center justify-center">
          <ShieldAlert className="w-3.5 h-3.5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-700">New intel</div>
          <div className="text-sm font-display font-bold">Room 412 · check-out incident</div>
        </div>
      </div>

      <div className="bg-white border border-border rounded-md p-4 space-y-3 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Guest</div>
            <div className="text-sm font-display font-bold">M. Alvarez · ID DL-8842-09</div>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-red-700 bg-red-100 px-2 py-0.5 rounded-sm">High</span>
        </div>

        <div className="space-y-1.5">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                className="flex items-center gap-2 text-xs animate-[slideIn_0.45s_ease-out_both]"
                style={{ animationDelay: `${i * 200}ms` }}
              >
                <div className="w-6 h-6 rounded-sm bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <span className="flex-1 text-zinc-700">{s.label}</span>
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              </div>
            );
          })}
        </div>
      </div>

      <button className="mt-auto inline-flex items-center justify-between gap-2 bg-primary text-primary-foreground rounded-md px-3 py-2 text-xs font-medium animate-[pulseGlow_2.4s_ease-in-out_infinite]" tabIndex={-1}>
        <span>Save intel</span> <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/* ---- Slide 3: Dashboard ---- */
function DashboardSlide() {
  const KPIS = [
    { label: "Records", value: "1,284", tone: "default" },
    { label: "Critical", value: "37", tone: "danger" },
    { label: "Do Not Rent", value: "212", tone: "danger" },
    { label: "Open", value: "8", tone: "warn" },
  ];
  // 6 months of incident volume bars
  const BARS = [22, 31, 27, 44, 38, 52];
  const max = Math.max(...BARS);
  return (
    <div className="h-full w-full p-6 flex flex-col gap-4 bg-gradient-to-br from-zinc-50 via-white to-teal/5">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-md bg-zinc-900 text-white flex items-center justify-center">
          <TrendingUp className="w-3.5 h-3.5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Group dashboard</div>
          <div className="text-sm font-display font-bold">Meridian Group · this quarter</div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {KPIS.map((k, i) => (
          <div
            key={k.label}
            className="bg-white border border-border rounded-md px-2.5 py-2 animate-[fadeIn_0.45s_ease-out_both]"
            style={{ animationDelay: `${i * 80}ms` }}
          >
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{k.label}</div>
            <div className={`font-display font-black text-lg leading-tight ${k.tone === "danger" ? "text-red-600" : k.tone === "warn" ? "text-amber-600" : "text-zinc-900"}`}>
              {k.value}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-border rounded-md p-3 flex-1 flex flex-col">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Last 6 months · incidents</div>
        <div className="flex items-end gap-1.5 flex-1 h-24">
          {BARS.map((v, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div
                className="w-full bg-primary rounded-t-sm origin-bottom animate-[barGrow_0.6s_ease-out_both]"
                style={{ height: `${(v / max) * 100}%`, animationDelay: `${i * 70}ms` }}
              />
              <div className="text-[9px] text-muted-foreground font-mono">{`M${i + 1}`}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 text-[11px] text-teal">
        <span className="w-1.5 h-1.5 bg-teal rounded-full animate-pulse" /> Network up · 38 properties reporting
      </div>
    </div>
  );
}

const SLIDE_COMPONENTS = [LookupSlide, CaptureSlide, DashboardSlide];

export { SLIDES, SLIDE_CAPTIONS, SLIDE_COMPONENTS, DEFAULT_INTERVAL_MS };

export default function HeroDemo({ intervalMs = DEFAULT_INTERVAL_MS, onSlideChange, height = "h-[420px]" }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % SLIDE_COMPONENTS.length), intervalMs);
    return () => clearInterval(t);
  }, [paused, intervalMs]);

  useEffect(() => { if (onSlideChange) onSlideChange(idx, SLIDES[idx]); }, [idx, onSlideChange]);

  return (
    <div
      className={`relative w-full ${height} bg-white`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-demo"
    >
      <PaginationDots active={idx} onSelect={setIdx} />
      {SLIDE_COMPONENTS.map((Slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? "opacity-100 z-[1]" : "opacity-0 z-0 pointer-events-none"}`}
          data-testid={`hero-slide-${SLIDES[i]}`}
          aria-hidden={i !== idx}
        >
          <Slide />
        </div>
      ))}
    </div>
  );
}
